import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { ExternalLink, FileText } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useCompanyDocuments, COMPANY_DOCS_BUCKET, CompanyDocument } from '@/hooks/useCompanyDocuments';

export const CompanyDocumentsCard = ({ companyId }: { companyId?: string }) => {
  const { t } = useTranslation();
  const { documents, isLoading } = useCompanyDocuments(companyId);

  if (!companyId) return null;

  const open = async (doc: CompanyDocument) => {
    const { data } = await supabase.storage.from(COMPANY_DOCS_BUCKET).createSignedUrl(doc.file_url, 600);
    if (data?.signedUrl) window.open(data.signedUrl, '_blank');
    else toast.error(t('adminDocuments.openError'));
  };

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle className="font-['Lexend_Deca'] text-[#37699E]">
          {t('settings.companyDocuments.title')}
        </CardTitle>
        <CardDescription>{t('settings.companyDocuments.description')}</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3">{[1, 2].map((i) => <Skeleton key={i} className="h-12" />)}</div>
        ) : documents.length === 0 ? (
          <div className="text-center py-8 text-[#5A6B7A]">
            <FileText className="mx-auto mb-3" size={32} />
            <p className="text-sm font-['Funnel_Sans']">{t('settings.companyDocuments.empty')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {documents.map((d) => (
              <div
                key={d.id}
                className="flex flex-wrap items-center justify-between gap-3 border border-[#DBE2EA] rounded-lg p-3"
              >
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-[#2D3748] truncate font-['Funnel_Sans']">
                    {d.title || d.file_name}
                  </div>
                  <div className="text-xs text-[#5A6B7A]">
                    {format(new Date(d.created_at), 'dd/MM/yyyy HH:mm')}
                    {d.expiry_date && ` · ${t('adminDocuments.licenseExpires')} ${format(new Date(d.expiry_date), 'dd/MM/yyyy')}`}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-[#0880AE]/10 text-[#0880AE] border-[#0880AE]/20">
                    {t(`adminDocuments.categories.${d.category}`, { defaultValue: t('adminDocuments.categories.other') })}
                  </Badge>
                  <Button variant="ghost" size="sm" onClick={() => open(d)}>
                    <ExternalLink size={16} className="text-[#0880AE]" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
